import React, { useState } from 'react';
import { Form, Input } from 'antd';
import { appMessage as message } from '../../utils/appMessage';
import axios from 'axios';
import { LockOutlined, UserOutlined } from '@ant-design/icons';
import { login } from '../../api/api';
import { useStorage } from '../../contexts/StorageContext';
import { jwtSubject } from '../../utils/jwt';
import { AppButton } from '../common/AppButton';

/**
 * 로그인 실패 사유별 안내 메시지. "아이디/비번이 틀렸다"와 "서버에 아예 닿지 않는다"를
 * 구분하지 않으면, 백엔드 장애 중에도 사용자가 비밀번호 문제로 오해해 헛되이 재입력을
 * 반복하게 된다(2026-09-04 실사고 — 잡 진행률 맵 무한 적재로 OOM이 나 API가 몇 시간째
 * 무응답이었는데, 그 시간 동안 로그인 화면은 계속 "아이디 또는 비밀번호가 올바르지
 * 않습니다"만 띄웠을 것).
 *
 * - 서버가 응답했다(`error.response` 있음) + 401/기타 인증 실패 → 자격 증명 문제
 * - 서버가 응답했다 + 5xx → 백엔드가 떠 있긴 하나 내부에서 에러
 * - 서버가 응답을 못 냈다(`error.response` 없음 — 타임아웃/연결거부/네트워크 끊김) →
 *   접속 자체가 안 되는 상태(오늘 사고가 정확히 이 경우 — TCP는 받되 무응답)
 */
function resolveLoginErrorMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    if (!err.response) {
      return '서버에 접속할 수 없습니다. 잠시 후 다시 시도해 주세요.';
    }
    if (err.response.status >= 500) {
      return '서버에 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.';
    }
  }
  return '아이디 또는 비밀번호가 올바르지 않습니다.';
}

/** 로그인 폼. 레이아웃은 포함하지 않는다 — LoginPage 에서 AuthLayout 으로 감싼다. */
export const LoginForm: React.FC = () => {
  const { handleLogin } = useStorage();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (values: { id: string; password: string }) => {
    setLoading(true);
    try {
      // 토큰은 백엔드가 HttpOnly 쿠키로 내려준다(2026-08-19) — 여기서 localStorage에
      // 저장하지 않는다. role/store만 화면 권한 제어·매장 표시용으로 받는다. 계정 아이디는
      // 응답의 username(있으면) → 응답 토큰의 sub → 입력한 아이디 순으로 정해 사용자별 키에 쓴다
      // (2026-10-21, 버전 알림 "이미 봤음"을 역할·매장이 아니라 계정 단위로).
      const res = await login(values.id, values.password);
      const username = res.username ?? jwtSubject(res.token) ?? values.id;
      handleLogin(res.role, res.store, username);
    } catch (err) {
      message.error(resolveLoginErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form onFinish={handleSubmit} layout="vertical" size="large">
      <Form.Item
        name="id"
        label="아이디"
        rules={[{ required: true, message: '아이디를 입력하세요.' }]}
      >
        <Input prefix={<UserOutlined />} placeholder="아이디" />
      </Form.Item>

      <Form.Item
        name="password"
        label="비밀번호"
        rules={[{ required: true, message: '비밀번호를 입력하세요.' }]}
      >
        <Input.Password prefix={<LockOutlined />} placeholder="비밀번호" />
      </Form.Item>

      <Form.Item style={{ marginBottom: 0 }}>
        <AppButton variant="primary" size="lg" htmlType="submit" loading={loading} block>
          로그인
        </AppButton>
      </Form.Item>
    </Form>
  );
};